import { useState, useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Shield, Star, Award, Clock, Users, Droplets, MapPin, CheckCircle2, Check } from "lucide-react";
import { toast } from "sonner";
import { getRecaptchaToken } from "@/hooks/useRecaptcha";
import { projectDetailsSchema } from "@/lib/projectDetails";
import { LazySection } from "@/components/LazySection";
import { REVIEW_COUNT } from "@/config/reviews";


import logo from "@/assets/masonry-pro-logo-3.webp";
import njccLogo from "@/assets/njcc-logo.webp";
// Hero — temporary placeholder per spec; swap the import target to update the image.
import heroImage from "@/assets/mp-breezewood-retaining-wall-installation-paramus-nj-04052026-2.webp";
import heroImageMobile from "@/assets/mp-breezewood-retaining-wall-paramus-mobile.webp";
// Retaining-wall gallery
import rw1 from "@/assets/retaining-wall-installation-nutley-essex-county-nj-2.webp";
import rw2 from "@/assets/retaining-wall-hardscape-old-tappan-bergen-county-nj.webp";
import rw3 from "@/assets/ep-henry-stone-retaining-wall-franklin-lakes-nj.webp";
import rw4 from "@/assets/retaining-wall-morris-township-morris-county-nj.webp";
import rw5 from "@/assets/retaining-wall-steps-wayne-passaic-county-nj.webp";
import rw6 from "@/assets/retaining-wall-installation-nutley-nj-2.webp";
// Bottom-row retaining wall projects
import adj1 from "@/assets/mp-retaining-wall-construction-totowa-nj-04052026.webp";
import adj2 from "@/assets/mp-breezewood-retaining-wall-installation-paramus-nj-04052026.webp";
import adj3 from "@/assets/mp-retaining-wall-hackensack-nj-04052026.webp";

const BeforeAfterCarousel = lazy(() => import("@/components/lp/BeforeAfterCarouselRW"));

/* ─── UTM / GCLID helpers ─── */
function getUrlParams() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_term: p.get("utm_term") || "",
    utm_content: p.get("utm_content") || "",
    gclid: p.get("gclid") || "",
  };
}

/* ─── Lead Form ─── */
function LeadForm({
  id,
  source = "Landing Page - Retaining Walls",
  formName = "Retaining Walls LP Form",
  pagePath = "/lp/retaining-walls",
}: { id: string; source?: string; formName?: string; pagePath?: string }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zipCode: "", message: "" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    const details = projectDetailsSchema.safeParse(form.message);
    if (!details.success) {
      toast.error(details.error.issues[0]?.message || "Please tell us about your project.");
      return;
    }
    if (!consent) { setShowConsentError(true); return; }
    setIsSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken("submit_lead");
      const params = getUrlParams();
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.functions.invoke("submit-lead", {
        body: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          city: form.city,
          message: form.message,
          address: form.address,
          state: form.state,
          zipCode: form.zipCode,
          source,
          privacyConsent: consent,
          smsConsent: false,
          recaptchaToken,
          ...params,
        },
      });
      if (error) throw error;
      setSubmitted(true);
      if (typeof window !== "undefined") {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "form_submit",
          form_name: formName,
          form_source: source,
          page_path: pagePath,
        });
        (window as any).dataLayer.push({
          event: "lead_form_submit",
          form_name: "retaining_wall",
        });
      }
    } catch {
      toast.error("Something went wrong. Please call or text us at (973) 725-2395.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 space-y-4">
        <CheckCircle2 className="mx-auto h-12 w-12 text-secondary" />
        <h3 className="text-xl font-bold text-foreground">Thanks. Biagio or Gino will be in touch within one business day.</h3>
        <p className="text-muted-foreground">
          Want to talk now?{" "}
          <a href="tel:+19737252395" onClick={trackPhoneClick} className="text-secondary underline font-semibold">Call (973) 725-2395</a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
        <input name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>
      <div>
        <Label htmlFor={`${id}-name`} className="sr-only">Name</Label>
        <Input id={`${id}-name`} name="name" placeholder="Name*" required value={form.name} onChange={handleChange} className="bg-white text-black border-border h-12" />
      </div>
      <div>
        <Label htmlFor={`${id}-email`} className="sr-only">Email</Label>
        <Input id={`${id}-email`} name="email" type="email" placeholder="Email*" required value={form.email} onChange={handleChange} className="bg-white text-black border-border h-12" />
      </div>
      <div>
        <Label htmlFor={`${id}-phone`} className="sr-only">Phone Number</Label>
        <Input id={`${id}-phone`} name="phone" type="tel" placeholder="Phone Number*" required value={form.phone} onChange={handleChange} className="bg-white text-black border-border h-12" />
      </div>
      <div>
        <Label htmlFor={`${id}-address`} className="sr-only">Address</Label>
        <Input id={`${id}-address`} name="address" placeholder="Address*" required value={form.address} onChange={handleChange} className="bg-white text-black border-border h-12" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor={`${id}-city`} className="sr-only">City</Label>
          <Input id={`${id}-city`} name="city" placeholder="City*" required value={form.city} onChange={handleChange} className="bg-white text-black border-border h-12" />
        </div>
        <div>
          <Label htmlFor={`${id}-state`} className="sr-only">State</Label>
          <Input id={`${id}-state`} name="state" placeholder="State*" required value={form.state} onChange={handleChange} className="bg-white text-black border-border h-12" />
        </div>
        <div>
          <Label htmlFor={`${id}-zipCode`} className="sr-only">Zip Code</Label>
          <Input id={`${id}-zipCode`} name="zipCode" placeholder="Zip Code*" required value={form.zipCode} onChange={handleChange} className="bg-white text-black border-border h-12" />
        </div>
      </div>
      <div>
        <Label htmlFor={`${id}-message`} className="sr-only">Tell us about your project</Label>
        <Textarea id={`${id}-message`} name="message" placeholder="Size, location, timeline, any specifics...*" rows={4} required maxLength={500} value={form.message} onChange={handleChange} className="bg-white text-black border-border resize-none" />
      </div>
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <Checkbox id={`${id}-consent`} checked={consent} onCheckedChange={(c) => { setConsent(c === true); if (c) setShowConsentError(false); }} className="mt-1" />
          <Label htmlFor={`${id}-consent`} className="text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
            I agree to be contacted by Masonry Pro Construction about my project.
          </Label>
        </div>
        {showConsentError && <p className="text-sm text-destructive ml-6">Please agree to be contacted to submit your request.</p>}
      </div>
      <p className="text-xs text-muted-foreground text-center">* Required. Good details now mean an honest, accurate estimate later.</p>
      <Button type="submit" variant="gold" size="lg" className="w-full text-base font-bold" disabled={isSubmitting}>
        {isSubmitting ? "Sending..." : "Send My Request"}
      </Button>
      <p className="text-xs text-muted-foreground italic text-center">No trip fee. No pressure.</p>
      <p className="text-xs text-muted-foreground text-center leading-snug">
        This site is protected by reCAPTCHA and the Google{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a> apply.
      </p>
    </form>
  );
}

/* ─── Gallery images ─── */
const galleryImages = [
  { src: rw1, alt: "Retaining wall installation with stone steps in Nutley, NJ", caption: "Nutley, Essex County" },
  { src: rw2, alt: "Tiered retaining wall hardscape with steps and landscaping in Old Tappan, NJ", caption: "Old Tappan, Bergen County" },
  { src: rw3, alt: "EP Henry stone retaining wall in Franklin Lakes, NJ", caption: "Franklin Lakes, Bergen County" },
  { src: rw4, alt: "Modular retaining wall along a driveway in Morris Township, NJ", caption: "Morris Township, Morris County" },
  { src: rw5, alt: "Retaining wall with integrated steps in Wayne, NJ", caption: "Wayne, Passaic County" },
  { src: rw6, alt: "Retaining wall installation with steps and railing in Nutley, NJ", caption: "Nutley, Essex County" },
  { src: adj1, alt: "Tiered block retaining wall construction in Totowa, NJ", caption: "Totowa, Passaic County" },
  { src: adj2, alt: "Breezewood block retaining wall installation in Paramus, NJ", caption: "Paramus, Bergen County" },
  { src: adj3, alt: "Tiered retaining wall with steps in Hackensack, NJ", caption: "Hackensack, Bergen County" },
];

/* ─── FAQ data ─── */
const faqs = [
  { q: "How much does a retaining wall cost in North Jersey?", a: "Retaining wall pricing depends on height, length, materials, and site conditions. Block walls cost less than natural stone. Walls over four feet require engineering, which adds to the project. We give every homeowner a written estimate after we walk the property so you see exact numbers, not a range pulled out of thin air." },
  { q: "Do I need a permit for a retaining wall in North Jersey?", a: "Walls under four feet typically require a zoning permit only. Walls four feet or taller require construction permits and engineering approval in most New Jersey municipalities. We handle the permitting and coordinate with engineers when the project calls for it." },
  { q: "How long does a retaining wall installation take?", a: "Most residential walls take three to seven working days once we break ground. Larger projects, walls over four feet, or walls with integrated steps and lighting take longer. We give you a project timeline as part of the written estimate." },
  { q: "How long will my retaining wall last?", a: "Concrete and natural stone walls last 50 to 100 years. Segmental block walls last 25 to 50 years. The difference is usually the installation, not the material. Proper base prep and drainage are what separate a wall that holds for decades from one that fails in five years." },
  { q: "What causes retaining walls to fail?", a: "Poor drainage is the number one cause. Water builds pressure behind the wall and pushes it forward. Inadequate base prep, missing reinforcement, and improper soil compaction are the other big ones. Almost every failure we get called to repair traces back to shortcuts during the original install." },
  { q: "Do you repair existing retaining walls or only build new ones?", a: "Both. Sometimes a wall can be stabilized and repaired. Sometimes it needs to come down and be rebuilt. We'll walk the site and give you a straight answer either way. No upsell pressure." },
  { q: "What areas do you serve?", a: "We serve homeowners across Passaic, Bergen, Essex, and Morris counties. Totowa, Wayne, Clifton, Paramus, Ridgewood, Montclair, Nutley, Parsippany, Morristown, and dozens more. If you're in North Jersey, we're in your neighborhood." },
];

/* ─── Reviews ─── */
const reviews = [
  { text: "After getting several estimates, we chose Masonry Pro and couldn't be happier. They built a stunning stone retaining wall that solved our drainage issues while adding incredible curb appeal.", name: "Tommy F." },
  { text: "Fixed our leaning retaining wall that another company had botched. They did it right with proper drainage and reinforcement. It's been three years and hasn't moved an inch.", name: "Blake B." },
  { text: "Had them repair tuckpointing on our chimney and it looks better than when the house was built. Fair pricing, clean work, and they finished ahead of schedule.", name: "Craig A." },
];

/* ─── Service Areas ─── */
const areas = [
  { county: "Bergen County", towns: ["Paramus", "Ridgewood", "Hackensack", "Fair Lawn", "Glen Rock", "Wyckoff"] },
  { county: "Passaic County", towns: ["Totowa", "Wayne", "Clifton", "Little Falls", "Woodland Park", "Hawthorne"] },
  { county: "Essex County", towns: ["Montclair", "Nutley", "Bloomfield", "West Orange", "Verona", "Cedar Grove"] },
  { county: "Morris County", towns: ["Parsippany", "Denville", "Boonton", "Mountain Lakes", "Morristown"] },
];

/* ─── Steps ─── */
const steps = [
  { n: 1, title: "You Reach Out", desc: "Fill out the form or call us. We respond within one business day." },
  { n: 2, title: "We Walk Your Property", desc: "Biagio or Gino comes out, looks at the site, answers your questions. No pressure." },
  { n: 3, title: "You Get a Written Estimate", desc: "Within 48 hours. Clear pricing. No surprises later." },
  { n: 4, title: "We Build It", desc: "Once you give the go-ahead, we put you on the schedule and build a wall that holds." },
];

/* ─── Why Choose ─── */
const whyCards = [
  { icon: Users, title: "Family-Owned Since Day One", desc: "Biagio and Gino. Father and son. Every wall has our name on it." },
  { icon: Clock, title: "35+ Years in North Jersey", desc: "We know the freeze-thaw cycles, the clay soil, and the slopes. We build for our conditions." },
  { icon: Droplets, title: "Drainage-First Construction", desc: "The reason our walls don't lean. Water pressure is the number one cause of retaining wall failure. We plan for it before the first block goes down." },
  { icon: Shield, title: "Free Site Evaluation. No Trip Fees.", desc: "We come to you, walk the slope, and send a written estimate within 48 hours." },
];

/* ─── JSON-LD ─── */
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Masonry Pro Construction",
  telephone: "+1-973-725-2395",
  address: { "@type": "PostalAddress", addressLocality: "Totowa", addressRegion: "NJ", addressCountry: "US" },
  url: "https://masonry-pro.com",
  aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: String(REVIEW_COUNT) },
  areaServed: [
    { "@type": "AdministrativeArea", name: "Bergen County, NJ" },
    { "@type": "AdministrativeArea", name: "Passaic County, NJ" },
    { "@type": "AdministrativeArea", name: "Essex County, NJ" },
    { "@type": "AdministrativeArea", name: "Morris County, NJ" },
  ],
};

/* ─── Scroll helper ─── */
function scrollToForm() {
  document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" });
}

/* ─── Tracking helpers ─── */
function trackPhoneClick() {
  if (typeof window === "undefined") return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: "phone_call_click" });
}
function trackSmsClick() {
  if (typeof window === "undefined") return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: "Click - SMS Text" });
}

/* ======================== PAGE ======================== */
export default function LPRetainingWalls() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Deep-link anchors (/lp/retaining-walls#reviews etc.) — scroll after layout
  // settles, offset for the 64px fixed header.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    let tries = 0;
    const go = () => {
      const el = document.getElementById(hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top, behavior: "auto" });
      }
      if (++tries < 5) setTimeout(go, 150);
    };
    setTimeout(go, 50);
  }, []);

  return (
    <>
      <Helmet>
        <title>Custom Retaining Walls in North Jersey | Masonry Pro</title>
        <meta name="description" content="Retaining wall installation and repair in Bergen, Passaic, Essex, and Morris counties. Drainage-first construction. 35+ years experience. NJ Licensed. Free estimates. Call (973) 725-2395." />
        <meta name="robots" content="noindex, follow" />
        <link rel="preload" as="image" href={heroImageMobile} media="(max-width: 767px)" fetchPriority="high" />
        <link rel="preload" as="image" href={heroImage} media="(min-width: 768px)" fetchPriority="high" />
        <link rel="canonical" href="https://masonry-pro.com/lp/retaining-walls" />
        <meta property="og:title" content="Custom Retaining Walls in North Jersey | Masonry Pro" />
        <meta property="og:description" content="Retaining wall installation and repair in Bergen, Passaic, Essex, and Morris counties. Drainage-first construction. 35+ years experience. NJ Licensed. Free estimates. Call (973) 725-2395." />
        <meta property="og:url" content="https://masonry-pro.com/lp/retaining-walls" />
        <meta property="og:type" content="website" />
        {/* <!-- Google Ads global site tag placeholder --> */}
        {/* <!-- Google Ads conversion tracking tag placeholder --> */}
        {/* <!-- Google Analytics 4 tracking tag placeholder --> */}
        {/* <!-- Google Tag Manager container placeholder --> */}
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <meta property="og:image" content="https://masonry-pro.com/og/retaining-walls.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/retaining-walls.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        {/* ── 1. Sticky Top Bar ── */}
        <header className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"}`}>
          <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-16">
            <a href="/lp/retaining-walls" aria-label="Masonry Pro - back to top">
              <img src={logo} alt="Masonry Pro Construction logo" width={140} height={40} className="h-10 w-auto" />
            </a>
            <a href="tel:+19737252395" onClick={trackPhoneClick} className="flex items-center gap-2 text-[hsl(var(--secondary))] font-bold hover:underline">
              <Phone className="h-5 w-5" />
              <span className="hidden sm:inline">(973) 725-2395</span>
              <span className="sm:hidden text-sm">Call</span>
            </a>
          </div>
        </header>

        {/* spacer for fixed header */}
        <div className="h-16" />

        {/* ── 2. Hero — Full-width image with floating form ── */}
        <section className="relative">
          {/* Full-width background image */}
          <div className="absolute inset-0">
            <img
              src={heroImage}
              srcSet={`${heroImageMobile} 900w, ${heroImage} 1179w`}
              sizes="100vw"
              alt="Custom retaining wall installation in North Jersey"
              width={1179}
              height={777}
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
            <div className="md:flex md:items-center md:gap-10">
              {/* Left — Hero Text */}
              <div className="md:w-[58%] text-white mb-10 md:mb-0">
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4"><span className="text-[hsl(var(--secondary))]">Custom Retaining Walls</span> in North Jersey</h1>
                <p className="text-lg md:text-xl text-white/90 max-w-xl mb-6">
                  Family-owned and built to hold. 35+ years of retaining wall installation and repair across Bergen, Passaic, Essex, and Morris counties.
                </p>
                {/* Trust Bar — pill style */}
                <div className="inline-flex flex-wrap items-center gap-x-5 gap-y-1.5 bg-[hsl(220,30%,8%)]/80 backdrop-blur-sm rounded-2xl px-5 py-3 text-sm text-white/90 mb-6">
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--secondary))]" /> 35+ Yrs Experience</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--secondary))]" /> 5-Star Rated</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--secondary))]" /> Licensed & Insured</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--secondary))]" /> Free Estimates</span>
                </div>
                {/* Value Props */}
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-base text-white"><Check className="h-5 w-5 text-[hsl(var(--secondary))] flex-shrink-0" /> Free on-site site evaluation within 48 hours</li>
                  <li className="flex items-center gap-2 text-base text-white"><Check className="h-5 w-5 text-[hsl(var(--secondary))] flex-shrink-0" /> No trip fees, ever</li>
                  <li className="flex items-center gap-2 text-base text-white"><Check className="h-5 w-5 text-[hsl(var(--secondary))] flex-shrink-0" /> Written quote before any commitment</li>
                </ul>
                <a href="tel:+19737252395" onClick={trackPhoneClick} className="text-[hsl(var(--secondary))] hover:text-[hsl(var(--secondary))]/80 underline text-base font-bold">Or call (973) 725-2395</a>
              </div>

              {/* Right — Floating Form Card */}
              <div id="lead-form" className="md:w-[42%]">
                <Card className="w-full shadow-xl rounded-xl bg-[hsl(220,30%,8%)] border-none">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="text-xl md:text-2xl font-bold text-center mb-1 text-white">Get Your <span className="text-[hsl(var(--secondary))]">Free Retaining Wall Estimate</span></h2>
                    <p className="text-sm text-white/70 text-center mb-6">Biagio or Gino will reach out within one business day.</p>
                    <LeadForm id="hero" />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. Intro Block ── */}
        <section id="trust" className="py-14 bg-background">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
              Leaning Like the <span className="text-[hsl(var(--secondary))]">Tower of Pisa</span>? Or Just Tired of Looking at That Slope?
            </h2>
            <div className="space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              <p>
                Some homeowners find us because their wall is failing. Cracks running through the blocks, the whole thing tilting forward a little more each season, soil spilling out where it shouldn't. If that's you, we get it. A wall that's giving up doesn't fix itself, and it usually doesn't get cheaper to deal with later.
              </p>
              <p>
                Other homeowners find us before they have a wall at all. Maybe the backyard slopes away and half of it is unusable. Maybe a corner of the property erodes every time it rains. Maybe you just want a clean tiered look that turns a hillside into a place you can actually stand on without sliding.
              </p>
              <p>
                Either way, you're in the right place. We've built and repaired retaining walls all over North Jersey for 35+ years. Drainage first, base done right, materials that hold up to our freeze-thaw winters. We'll walk your property, give you a straight answer, and put it in writing within 48 hours.
              </p>
            </div>
          </div>
        </section>

        {/* ── 4. Why Choose ── */}
        <section id="about" className="py-16 bg-background">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Why North Jersey Homeowners <span className="text-[hsl(var(--secondary))]">Choose Masonry Pro</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyCards.map((c) => (
                <Card key={c.title} className="text-center">
                  <CardContent className="p-6 space-y-3">
                    <c.icon className="mx-auto h-10 w-10 text-secondary" />
                    <h3 className="font-bold text-lg">{c.title}</h3>
                    <p className="text-sm text-muted-foreground">{c.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Trusted & Verified Strip (no links) ── */}
        <section className="bg-[#2a3444] py-8 md:py-12 border-y border-border">
          <div className="container mx-auto px-4">
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-bold tracking-wide text-secondary uppercase">
                TRUSTED &amp; VERIFIED
              </h3>
              <p className="text-[20px] text-white font-medium mt-1">
                Accredited Masonry Contractor in North Jersey
              </p>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex justify-center">
                  <div className="flex w-[260px] h-[80px] bg-white border-2 border-[#0a2540] rounded overflow-hidden">
                    <div className="flex flex-col items-center justify-center w-[70px] bg-[#c9a961] text-[#0a2540] shrink-0">
                      <svg aria-hidden="true" width="24" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1L2 5v8c0 7.5 4.3 11.5 10 13 5.7-1.5 10-5.5 10-13V5L12 1z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M8 14l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                      <span className="text-[9px] tracking-[1px] mt-1 font-bold">VERIFIED</span>
                    </div>
                    <div className="flex flex-col justify-center px-3.5 py-2.5">
                      <p className="text-[10px] tracking-[1px] font-medium text-[#666] leading-tight">NJ LICENSED CONTRACTOR</p>
                      <p className="text-[13px] font-medium text-[#0a2540] mt-0.5 leading-tight">Home Improvement</p>
                      <p className="text-[11px] text-[#333] mt-1 leading-tight">License #13VH06777200</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="flex items-center w-[260px] h-[80px] bg-white border border-[#dadce0] rounded-lg px-[18px] py-[14px] box-border gap-3.5 overflow-hidden">
                    <img
                      src={njccLogo}
                      alt="North Jersey Chamber of Commerce member"
                      width="120"
                      height="52"
                      loading="lazy"
                      className="h-12 w-auto object-contain shrink-0"
                    />
                    <div className="min-w-0 flex flex-col justify-center">
                      <p className="text-[10px] tracking-[1px] font-medium text-[#5f6368] leading-tight">MEMBER</p>
                      <p className="text-[12px] font-semibold text-[#2a3444] leading-tight">Chamber of Commerce</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="flex items-center w-[260px] h-[80px] bg-white border border-[#dadce0] rounded-lg px-[18px] py-[14px] box-border gap-3.5">
                    <div className="pr-3.5 border-r border-[#e8eaed]">
                      <div className="bg-[#015A75] text-white font-bold text-[16px] px-3 py-2 rounded tracking-[1px]">BBB</div>
                    </div>
                    <div className="flex flex-col justify-center">
                      <div className="flex items-center gap-1">
                        <span className="text-[22px] font-medium text-[#015A75]">A+</span>
                        <span className="text-[13px] text-[#5f6368] ml-1">Rating</span>
                      </div>
                      <p className="text-[12px] text-[#5f6368] mt-0.5 leading-tight">Better Business Bureau</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="flex items-center w-[260px] h-[80px] bg-white border border-[#dadce0] rounded-lg px-[18px] py-[14px] box-border gap-3.5">
                    <div className="pr-3.5 border-r border-[#e8eaed]">
                      <svg aria-hidden="true" width="32" height="32" viewBox="0 0 48 48">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      </svg>
                    </div>
                    <div className="flex flex-col justify-center">
                      <div className="flex items-center gap-1">
                        <span className="text-[18px] font-medium text-[#202124]">5.0</span>
                        <div className="flex gap-px">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="h-3.5 w-3.5 fill-[#FBBC05] text-[#FBBC05]" aria-hidden="true" />
                          ))}
                        </div>
                      </div>
                      <p className="text-[12px] text-[#5f6368] mt-0.5 leading-tight">{`${REVIEW_COUNT}+ Google Reviews`}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. Recent Projects Gallery ── */}
        <section id="projects" className="py-16 bg-muted" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 1200px" } as any}>
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">Recent Projects Across <span className="text-[hsl(var(--secondary))]">North Jersey</span></h2>
            <p className="text-muted-foreground text-center mb-10">Real homes. Real transformations.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryImages.map((img) => (
                <div key={img.caption + img.src} className="group overflow-hidden rounded-xl aspect-square bg-muted">
                  <img src={img.src} alt={img.alt} width={600} height={600} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" decoding="async" />
                  <p className="text-xs text-muted-foreground text-center mt-2">{img.caption}</p>
                </div>
              ))}
            </div>

            {/* Before & After — scrolling carousel */}
            <div className="mt-12">
              <LazySection minHeight="500px">
                <Suspense fallback={null}>
                  <BeforeAfterCarousel />
                </Suspense>
              </LazySection>
            </div>
          </div>
        </section>

        {/* ── 6. Reviews ── */}
        <section id="reviews" className="py-16 bg-background" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 600px" } as any}>
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">What Our <span className="text-[hsl(var(--secondary))]">Customers Say</span></h2>
            <p className="text-muted-foreground text-center mb-10">{`${REVIEW_COUNT}+ five-star reviews from North Jersey homeowners.`}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <Card key={r.name}>
                  <CardContent className="p-6 space-y-4">
                    <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-[hsl(var(--secondary))] text-[hsl(var(--secondary))]" />)}</div>
                    <p className="text-sm text-muted-foreground italic">"{r.text}"</p>
                    <div>
                      <p className="font-semibold text-sm">{r.name}</p>
                      <p className="text-xs text-muted-foreground">Verified Google Review</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="https://www.google.com/maps/place/Masonry+Pro+Construction" target="_blank" rel="noopener noreferrer" className="text-secondary font-semibold hover:underline">
                {`Read All ${REVIEW_COUNT}+ Reviews on Google →`}
              </a>
            </div>
          </div>
        </section>

        {/* ── 7. What to Expect ── */}
        <section id="process" className="py-16 bg-muted" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 500px" } as any}>
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">What to Expect When You <span className="text-[hsl(var(--secondary))]">Work With Us</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((s) => (
                <div key={s.n} className="text-center space-y-3">
                  <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg">{s.n}</div>
                  <h3 className="font-bold">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 8. FAQ ── */}
        <section id="faq" className="py-16 bg-background" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 700px" } as any}>
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Common <span className="text-[hsl(var(--secondary))]">Questions</span></h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left font-semibold text-sm md:text-base">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── 9. Service Areas ── */}
        <section id="service-areas" className="py-16 bg-muted" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 500px" } as any}>
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">Serving <span className="text-[hsl(var(--secondary))]">North Jersey Homeowners</span></h2>
            <p className="text-muted-foreground text-center mb-10">Proudly based in Totowa, serving families across four counties.</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {areas.map((a) => (
                <div key={a.county}>
                  <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5"><MapPin className="h-4 w-4 text-secondary" />{a.county}</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {a.towns.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 10. Final CTA ── */}
        <section id="bottom" className="py-20 bg-[hsl(220,30%,8%)]" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 300px" } as any}>
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Build a <span className="text-[hsl(var(--secondary))]">Wall That Holds</span>?</h2>
            <p className="text-white/70 mb-8">We're booking fast. Lock in your site evaluation before the calendar fills.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button variant="gold" size="lg" className="font-bold px-8" onClick={scrollToForm}>Get My Free Estimate</Button>
              <a href="tel:+19737252395" onClick={trackPhoneClick}>
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 font-bold px-8">Call (973) 725-2395</Button>
              </a>
            </div>
          </div>
        </section>

        {/* ── 11. Bottom Form ── */}
        <section id="estimate" className="bg-muted py-16" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 700px" } as any}>
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Get Your <span className="text-[hsl(var(--secondary))]">Free Estimate</span></h2>
            <Card className="relative">
              <CardContent className="p-6 md:p-8">
                <LeadForm id="bottom" />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── 12. Minimal Footer ── */}
        <footer className="bg-[hsl(220,30%,6%)] text-white/70 py-8" style={{ contentVisibility: "auto", containIntrinsicSize: "1px 200px" } as any}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <img src={logo} alt="Masonry Pro Construction" className="h-8 w-auto" />
              <p className="text-sm text-center">© 2026 Masonry Pro Construction. NJ HIC #13VH06777200</p>
              <a href="tel:+19737252395" onClick={trackPhoneClick} className="text-sm text-secondary font-semibold hover:underline">(973) 725-2395</a>
            </div>
            <div className="flex justify-center gap-6 mt-4 text-xs">
              <a href="https://masonry-pro.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Privacy Policy</a>
              <a href="https://masonry-pro.com/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Terms & Conditions</a>
            </div>
          </div>
        </footer>

        {/* ── Sticky Mobile CTA Bar ── */}
        <div className="fixed bottom-0 inset-x-0 z-50 md:hidden flex">
          <a href="tel:+19737252395" onClick={trackPhoneClick} className="flex-1 flex items-center justify-center gap-2 bg-[hsl(220,30%,8%)] text-white py-3 font-bold text-sm">
            <Phone className="h-4 w-4" /> Call Now
          </a>
          <button onClick={scrollToForm} className="flex-1 flex items-center justify-center gap-2 bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] py-3 font-bold text-sm">
            Get Estimate
          </button>
        </div>
      </div>
    </>
  );
}